import { useDisclosure } from "@chakra-ui/react";
import {
  type BodyColumnDef,
  DataTable,
  TruncatedTextWithTooltip,
} from "@components/ui-kit";
import { t } from "@lingui/macro";
import {
  OtherDocumentOrderByColumnEnum,
  useGetOtherDocumentsQuery,
  useUpdateOtherDocumentMutation,
} from "@src/__generated__/graphql";
import { GoogleDriveLink } from "@src/components/ui-kit/GoogleDriveLink";
import { SharepointLink } from "@src/components/ui-kit/SharepointLink";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { FormMode } from "@src/utils/types";
import { filter } from "lodash";
import { observer } from "mobx-react-lite";
import type React from "react";
import {
  type FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ModalOtherDocumentForm } from "../../form/otherDocument/ModalOtherDocumentForm";
import {
  FormOtherDocumentCtx,
  StoreOtherDocumentForm,
} from "../../form/otherDocument/StoreOtherDocumentForm";
import type { OtherDocumentModel } from "../../models";
import { DocumentsCtx, type StoreDocuments } from "../../StoreDocuments";
import { CellDocuments } from "../CellDocuments";
import { CellFiles } from "../CellFiles";
import { PopoverRowActions } from "./PopoverRowActions";

/**
 * encapsulate initial data fetch & refetch
 */
function useData(store: StoreDocuments) {
  const { loading, error, refetch } = useGetOtherDocumentsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      ...store.pagination.asParams,
      filters: {
        orderBy: store.orderByMixed.otherDocuments.asWhereParam,
        where: store.whereMixed.otherDocuments.asGraphQueryParam,
        search: store.searchTerm || undefined,
      },
    },
    onCompleted(data) {
      store.fromGetOtherDocumentsQuery(data);
    },
    ...onError(),
  });

  store.refetchOtherDocs = refetch;
  store.error = error;
  store.loading = loading;
}

type TData = {
  id: OtherDocumentModel["id"];
  title: OtherDocumentModel["title"];
  files: OtherDocumentModel["files"];
  documents: string;
  relatedTo?: string[];
  actions: string;
  cloudUrl: OtherDocumentModel["cloudUrl"];
  driveCloudUrl: OtherDocumentModel["driveCloudUrl"];
  _model: OtherDocumentModel;
};

export const TableOtherDocuments = observer(function TableOtherDocuments() {
  const store = useContext(DocumentsCtx);

  useData(store);

  const data: TData[] = store.otherDocuments.map((r) => ({
    id: r.id,
    title: r.title,
    relatedTo: [
      ...r.projects.map(({ title }) => title),
      ...r.clients.map(({ name }) => name),
      ...r.contractors.map(({ legalName }) => legalName),
      ...r.brands.map(({ name }) => name),
    ],
    files: r.files,
    documents: "",
    actions: "",
    cloudUrl: r.cloudUrl,
    driveCloudUrl: r.driveCloudUrl,
    _model: r,
  }));

  const columns = useMemo<
    BodyColumnDef<TData, OtherDocumentOrderByColumnEnum>[]
  >(
    () => [
      {
        id: "title",
        meta: { label: t`Title` },
        header: () => t`Title`,
        orderBy: OtherDocumentOrderByColumnEnum.Title,
        cell: ({ row }) => <TitleCell r={row.original} />,
      },
      {
        id: "related-to",
        meta: { label: t`Related to` },
        header: () => t`Related to`,
        cell: ({ row }) => {
          const relatedToText = row.original.relatedTo?.join(", ");
          return (
            <TruncatedTextWithTooltip text={relatedToText} maxWidth="270px" />
          );
        },
      },
      {
        id: "files",
        meta: { label: t`Files` },
        header: () => t`Files`,
        cell: ({ row }) => <CellFiles files={row.original.files} />,
      },
      {
        id: "documents",
        meta: { label: t`Documents` },
        header: () => t`Documents`,
        cell: ({ row }) => (
          <CellDocuments
            data={{
              download: row.original._model.urls?.download,
              previews: row.original._model.files,
            }}
          />
        ),
      },
      {
        id: "sharepoint",
        meta: { label: t`Sharepoint` },
        header: () => t`Sharepoint`,
        cell: ({ row }) =>
          row.original.cloudUrl && (
            <SharepointLink href={row.original.cloudUrl} />
          ),
      },
      {
        id: "drive",
        meta: { label: t`Google Drive` },
        header: () => t`Google Drive`,
        cell: ({ row }) =>
          row.original.driveCloudUrl && (
            <GoogleDriveLink href={row.original.driveCloudUrl} />
          ),
      },
      {
        id: "actions",
        meta: { label: t`Actions` },
        header: () => t`Actions`,
        cell: ({ row }) => <PopoverRowActions doc={row.original._model} />,
      },
    ],
    [],
  );

  const visibleColumns = useMemo(() => {
    if (store.asExternalModule) {
      return filter(
        columns,
        (col) =>
          !col.id ||
          !store.hideColsInExternalModule.otherDocuments.includes(col.id),
      );
    }
    return columns;
  }, [
    columns,
    store.asExternalModule,
    store.hideColsInExternalModule.otherDocuments,
  ]);

  return (
    <DataTable.Body
      tableKey="other-documents"
      data={data}
      columns={visibleColumns}
      orderedBy={store.orderByMixed.otherDocuments.clauses[0]}
      onOrderByChange={store.handleOtherDocsOrderByChange}
    />
  );
});

const TitleCell: FunctionComponent<React.PropsWithChildren<{ r: TData }>> =
  observer(function TitleCell({ r }) {
    const pageStore = useContext(DocumentsCtx);
    const [store] = useState(() => {
      const store = StoreOtherDocumentForm.create();
      return store;
    });

    const editModal = useDisclosure();

    const [update, updateReq] = useUpdateOtherDocumentMutation({
      onCompleted() {
        pageStore.refetchOtherDocs?.();
        editModal.onClose();
      },
      ...onError(),
    });

    useEffect(() => {
      if (editModal.isOpen) store.fillOtherDocumentForm(r._model);
    }, [editModal.isOpen]);

    return (
      <FormOtherDocumentCtx.Provider value={store}>
        {can("otherDocument_update") ? (
          <button
            {...editModal.getButtonProps()}
            className="Polaris-Link outline-none border-none"
          >
            {r.title}
          </button>
        ) : (
          <span>{r.title}</span>
        )}
        <ModalOtherDocumentForm
          {...editModal.getDisclosureProps()}
          {...editModal}
          title={t`Edit order`}
          mode={FormMode.EDIT}
          primaryActionContent={t`Save changes`}
          defaultContractorName={r._model.contractors?.[0]?.legalName}
          loading={updateReq.loading}
          onSubmit={(vars) => {
            update({ variables: { input: { ...vars, id: r._model.id } } });
          }}
        />
      </FormOtherDocumentCtx.Provider>
    );
  });
